import { Company } from "@models/companies/company";
import { Entity } from "@models/companies/entity";
import { Document } from "@models/documents/document";
import {
  Relationship,
  ShareholderRelationshipDetails,
  TrusteeRelationshipDetails
} from "@models/relationships/relationship";
import { RelationshipType, RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";
import { SecurityHolding } from "@models/shares/security-holding";
import { TrustSubtypesLabelsDictionary } from "@models/trusts/trusts-enums";
import { ChangeDictionaryHelper } from "@models/ui-models/change-dictionary-helper.model";
import { IPDFSection } from "@models/ui-models/pdf-section.interface";
import { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Guid } from "@shared/utils/guid.helper";
import { BaseTrustEstablishmentFormComponent } from "../../asic-forms/trusts";
import { getTrustEstablishmentChangeType } from "../../asic-forms/trusts/constants/trust-establishment-texts";
import { TrustEstablishmentBase } from "./trust-establishment-base.model";

export class TrustUnitEstablishment extends TrustEstablishmentBase {
  static override $type = 'TrustUnitEstablishment';
  securityHoldings: SecurityHolding[];

  constructor(data: Partial<TrustUnitEstablishment> = {}) {
    super(data);
    this.securityHoldings = (data.securityHoldings ?? []).map((holding) => new SecurityHolding(holding));
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    const instance = (modalRef.componentInstance as BaseTrustEstablishmentFormComponent<any, TrustUnitEstablishment>);

    instance.documentId = document.documentId;
    instance.entityId = document.entityId!;
    instance.isEdit = true;
    instance.formModel = new TrustUnitEstablishment(this);

    return this.fillModalWithCompany(modalRef, actualCompany, true);
  }

  override fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit: boolean): NgbModalRef {
    const instance = modalRef.componentInstance as BaseTrustEstablishmentFormComponent<any, TrustUnitEstablishment>;

    instance.entityId = company?.entityId ?? Guid.EmptyGuid;
    instance.trustType = this.trustType;
    instance.trustSubtype = this.subType;

    return modalRef;
  }

  override toDictionary(entity): { key: string, value: string }[] {
    const dict = new ChangeDictionaryHelper();

    dict
      .add('Trust Name', this.trustName)
      .add('Trust Type', TrustSubtypesLabelsDictionary[this.subType])
      .addJurisdiction('Jurisdiction', this.jurisdiction!)
      .add('Meeting Address', this.meetingAddress.normalizedFullAddress);

    this.trustees.forEach((relationship) => {
      const name = relationship.individualDataOverride?.fullName ?? relationship.entityDataOverride?.name;
      const acn = relationship.entityDataOverride?.entityNumber;
      const address = (relationship.individualDataOverride?.address ?? relationship.entityDataOverride?.registeredAddress)?.normalizedFullAddress;

      dict.addDivider();

      if (relationship.entityDataOverride) {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Company Name`, name)
          .addAcn('ACN', acn)
          .add('Registration Address', address);

        if (relationship.details && relationship.details.$type == TrusteeRelationshipDetails.$type && (relationship.details as TrusteeRelationshipDetails | null)?.authorisedSignatories?.length) {
          const details = relationship.details as TrusteeRelationshipDetails;
          const authSignatories = details.authorisedSignatories
            .map((signatory) => signatory.fullName.trim())
            .filter(Boolean)
            .join('\n');

          dict.add('Authorising Signatories', authSignatories);
        }
      } else {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Name`, name)
          .add('Address', address);
      }
    });

    this.securityHoldings.forEach((holding) => {
      const isBeneficialOwner = !holding.relationships?.some((r) => (r?.details as ShareholderRelationshipDetails)?.isBeneficialOwner);
      const beneficialOwner = (holding.relationships?.[0]?.details as ShareholderRelationshipDetails)?.beneficialOwner ?? '';

      dict
        .addDivider();

      holding.relationships?.forEach((relationship) => {
        dict
          .add('Unitholder', relationship.individualDataOverride?.fullName ?? relationship.entityDataOverride?.name ?? '')
          .add('Residential Address', relationship?.individualDataOverride?.address?.normalizedFullAddress ?? relationship.entityDataOverride?.registeredAddress?.normalizedFullAddress ?? '');

        if (relationship.details && relationship.details.$type == TrusteeRelationshipDetails.$type) {
          const details = relationship.details as TrusteeRelationshipDetails;
          const authSignatories = details.authorisedSignatories
            .map((signatory) => signatory.fullName.trim())
            .filter(Boolean)
            .join('\n');

          dict.add('Authorising Signatories', authSignatories);
        }
      });

      dict
        .add('Unit Class', holding.securityType.class)
        .add('Number of Units', holding.number)
        .addMoney('Total Amount Paid', holding.paid)
        .addMoney('Total Amount Unpaid', holding.unpaid)
        .addNoYes('Units are owned on behalf', !isBeneficialOwner)
        .addIfValueIsNotEmpty('Units are owned on behalf', beneficialOwner);
    });

    return dict.dictionary;
  }

  override prepareToPdf(entity: Entity): IPDFSection[] {
    const dict = new ChangeDictionaryHelper();

    dict
      .addSection('Trust Info')
      .add('Trust Name', this.trustName)
      .add('Trust Type', TrustSubtypesLabelsDictionary[this.subType])
      .addJurisdiction('Jurisdiction', this.jurisdiction!)
      .add('Meeting Address', this.meetingAddress.normalizedFullAddress);


    dict.addSection(RelationshipTypeLabels[RelationshipType.Trustee]);
    this.trustees.forEach((relationship, index) => {
      const name = relationship.individualDataOverride?.fullName ?? relationship.entity?.name;
      const acn = relationship.entity?.entityNumber;
      const address = (relationship.individualDataOverride?.address ?? relationship.entity?.registeredAddress)?.normalizedFullAddress;

      if (relationship.entity) {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Company Name`, name)
          .addAcn('ACN', acn)
          .add('Registration Address', address);
      } else if (relationship.individualDataOverride) {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Name`, name)
          .add('Address', address);
      }
    });

    // UNITHOLDERS SECTIONS
    this.securityHoldings.forEach((holding) => {
      const isBeneficialOwner = !holding.relationships?.some((r) => (r.details as ShareholderRelationshipDetails).isBeneficialOwner);
      const beneficialOwner = (holding.relationships?.[0]?.details as ShareholderRelationshipDetails).beneficialOwner ?? '';

      dict
        .addDivider();

      holding.relationships?.forEach((relationship) => {
        dict
          .add('Unitholder', relationship.individualDataOverride?.fullName ?? relationship.entityDataOverride?.name ?? '')
          .add('Residential Address', relationship?.individualDataOverride?.address?.normalizedFullAddress ?? relationship.entityDataOverride?.registeredAddress?.normalizedFullAddress ?? '');
      });

      dict
        .add('Unit Class', holding.securityType.class)
        .add('Number of Units', holding.number)
        .addMoney('Total Amount Paid', holding.paid)
        .addMoney('Total Amount Unpaid', holding.unpaid)
        .addNoYes('Units are owned on behalf', !isBeneficialOwner)
        .addIfValueIsNotEmpty('Units are owned on behalf', beneficialOwner);
    });

    return dict.sections;
  }

  override prepareToRequest(): TrustUnitEstablishment {
    const payload = super.prepareToRequest() as TrustUnitEstablishment;
    const prepareRelationshipsWithCompany = (relationships: Relationship[]) => {
      return relationships.map((relationship) => {
        if (relationship.entity) {
          const company = relationship.entity as Company;

          // @ts-ignore
          delete company.nextAnnualReviewDate;
        }

        return Relationship.prepareToRequest(relationship);
      });
    };

    payload.trustees = prepareRelationshipsWithCompany(payload.trustees);
    payload.securityHoldings = payload.securityHoldings.map((holding) => {
      if (holding.relationships) {
        holding.relationships = prepareRelationshipsWithCompany(holding.relationships);
      }

      return holding;
    });

    return payload;
  }

  override buildPreviewType(): string {
    return getTrustEstablishmentChangeType(this.subType)
      .replace('t:', '')
      .replaceAll('-', '');
  }
}
